import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {DateRange} from "@angular/material/datepicker";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {ConsoleContextService} from "@security/console-context.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {ConfirmationDialogComponent} from "@ui-kit/webkit/components/confirmation-dialog/confirmation-dialog.component";
import {TableActionItem} from "@ui-kit/webkit/components/crud-table/crud-table.model";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {ChartConfiguration, ChartData} from "chart.js";
import {BaseChartDirective} from "ng2-charts";
import {lastValueFrom, Subject} from "rxjs";
import {chartColors} from "../../../dashboards/chart-options/chart-colors.model";
import {barChartOptions, barChartPlugins} from "../../pipeline/pipeline-list/chart-config.constant";
import {SurveyViewComponent} from "../survey-view/survey-view.component";
import {SurveyService} from "../survey.service";
import {Survey} from "../surveys.model";
import {CreateSurveyDialogComponent} from "./create-survey-dialog/create-survey-dialog.component";
import {DuplicateSurveyDialogComponent} from "./duplicate-survey-dialog/duplicate-survey-dialog.component";

@Component({
  selector: 'app-manage-surveys',
  templateUrl: './manage-surveys.component.html'
})
export class ManageSurveysComponent extends BasePageComponent implements OnInit, OnDestroy {

  private _loader = inject(LoadingService);
  private _snackbarService = inject(SnackbarService);
  private _clearOnDestroy$ = new Subject<void>();
  private _cdr = inject(ChangeDetectorRef);
  private _tabService = inject(TabManagementService);
  public _utils = inject(UtilsService);
  public _dateUtils = inject(DateUtilsService);
  private _matDialog = inject(MatDialog);

  private _context = inject(ConsoleContextService);
  private _surveyService = inject(SurveyService);

  protected readonly surveyModelType = Survey;

  protected dateRange: DateRange<Date> = new DateRange<Date>(null, null);
  protected dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('all_time', 0, TimeFrames.DATE_RANGE, 'All time', false, new DateRange<Date>(new Date(this.offering!.offeringStartDate!), new Date())),
    new CustomDateRangeModel('this_month', 0, TimeFrames.MONTH, 'This month'),
    new CustomDateRangeModel('last_month', 1, TimeFrames.MONTH, 'Last month', true),
  ]

  protected addSurveyAction = new EventEmitter<any>();
  protected viewEditAction = new EventEmitter<any>();
  protected deleteSurveyAction = new EventEmitter<any>();
  protected duplicateSurveyAction = new EventEmitter<any>();
  protected refreshSurveyDataAction = new EventEmitter<any>();

  protected readonly surveysAdditionalActions: TableActionItem[] = [
    {
      icon: 'refresh',
      label: 'Refresh data',
      actionDataSource: 'None',
      enabled: "Always",
      includeData: false,
      eventEmitter: this.refreshSurveyDataAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'open_in_new',
      label: 'View/Edit',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.viewEditAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'add',
      label: 'Create new',
      actionDataSource: 'None',
      enabled: "Always",
      includeData: false,
      eventEmitter: this.addSurveyAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'file_copy',
      label: 'Duplicate',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.duplicateSurveyAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'remove',
      label: 'Delete',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.deleteSurveyAction,
      keepSelectedRowOnClick: false
    },
  ];

  protected loading$ = this._loader.loading$;
  protected pendingChanges: boolean = false;

  protected surveys: Survey[] = [];
  protected selectedSurvey: Survey | null = null;
  protected updatedSurveyRowData: Survey | null = null;

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  // @ts-ignore
  barChartOptions: ChartConfiguration['options'] = barChartOptions;
  barChartPlugins = barChartPlugins;

  overviewStats: {
    fullyCompletedApplications: number,
    partiallyCompletedApplications: number,
    chartData: ChartData<'bar'>
  } = {
    fullyCompletedApplications: 0,
    partiallyCompletedApplications: 0,
    chartData: {
      labels: [],
      datasets: [
        {data: []}
      ]
    }
  }

  overviewChartLoading: boolean = false;

  constructor(injector: Injector) {
    super(injector);

    this.refreshSurveyDataAction.subscribe((event: any) => {
      this.loadAllData();
    });

    this.addSurveyAction.subscribe((event: any) => {
      this.openCreateSurveyDialog();
    });

    this.viewEditAction.subscribe((event: any) => {
      this.openViewTab(event);
    });

    this.deleteSurveyAction.subscribe((event: any) => {
      this.openDeleteSurveyDialog(event);
    });

    this.duplicateSurveyAction.subscribe((event: any) => {
      this.openDuplicateSurveyDialog(event);
    });
  }

  ngOnInit(): void {
    // this.loadSurveys();
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  protected getDateRange(event: CustomDateRangeChangeEventModel) {
    this.dateRange = event.dateRange;
    this.loadAllData();
  }

  loadAllData() {
    this.loadChartData()
      .then(() => this.loadSurveys()
        .then(() => console.log('Successfully loaded survey data')));
  }

  protected surveysRowSelected($event: any) {
    const hasData: boolean = $event ?? false;
    this.selectedSurvey = hasData ? new Survey($event) : null;
  }

  private async loadSurveys(): Promise<void> {
    this.selectedSurvey = null;
    this.surveys = [];

    try {
      this.surveys = await lastValueFrom(this._surveyService.getAllSurveys(this.dateRange)
        .pipe(this._loader.withLoader(this._clearOnDestroy$)));
    } catch (err) {
      return console.log(err);
    }
  }

  //todo implement
  //also, set this.loading to true unitil the service responds or until the takeuntil(this._clearOnDestroy$) completes
  private async loadChartData(): Promise<void> {
    try {
      const value = await lastValueFrom(this._surveyService.getOverviewChartData(this.dateRange)
        .pipe(this._loader.withLoader(this._clearOnDestroy$)));
      const chartData = value.chartSeries[0].chartData;

      this.overviewStats.partiallyCompletedApplications = Object(value.params)['partiallyComplete'];
      this.overviewStats.fullyCompletedApplications = Object(value.params)['fullyComplete'];
      this.overviewStats.chartData = {
        labels: chartData.map(m => m.label),
        datasets: [
          {
            data: chartData.map(m_1 => m_1.data),
            backgroundColor: chartColors.BLUE,
            borderColor: chartColors.BLUE,
            borderRadius: 5
          },
        ]
      };
    } catch (err) {
      console.log(err);
    }
  }

  protected openCreateSurveyDialog(): void {
    this.updatedSurveyRowData = null;
    const dialogRef = this._matDialog.open(CreateSurveyDialogComponent, {
      panelClass: 'dialog-large',
      data: {
        offering: this.data.offering,
        clearOnDestroy$: this._clearOnDestroy$
      }
    });

    dialogRef.afterClosed().subscribe((createdSurvey: Survey) => {
      if (createdSurvey) {
        this.surveys.unshift(createdSurvey);
        this.updatedSurveyRowData = createdSurvey;
      }
    });
  }

  protected openDuplicateSurveyDialog(survey: Survey): void {
    this.updatedSurveyRowData = null;
    const dialogRef = this._matDialog.open(DuplicateSurveyDialogComponent, {
      panelClass: 'dialog-large',
      data: {
        survey: survey,
        clearOnDestroy$: this._clearOnDestroy$
      }
    });

    dialogRef.afterClosed().subscribe((duplicatedSurvey: Survey) => {
      if (duplicatedSurvey) {
        this.surveys.unshift(duplicatedSurvey);
        this.updatedSurveyRowData = duplicatedSurvey;
      }
    });
  }

  protected openDeleteSurveyDialog(event: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: 'Are you sure you want to delete the selected survey? You will no longer be able to see any submissions or stats relating to this survey.',
      buttonConfirmLabel: 'Delete Survey',
      buttonCancelLabel: 'Cancel'
    };

    const dialogRef = this._matDialog.open(ConfirmationDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this._surveyService.deleteSurvey(event.uuid)
          .pipe(this._loader.withLoader(this._clearOnDestroy$))
          .subscribe(() => {
            this.surveys = this.surveys.filter(f => f.uuid !== event.uuid);
            this.selectedSurvey = null;
            this.updatedSurveyRowData = null;
          });
      }
    });
  }

  //todo can change tp pass full survey obj to the component
  private openViewTab(survey?: Survey | null) {
    console.log('open view tab', survey?.uuid);
    // console.log(this.selectedSurvey);

    let payload: PageEventInterface = {
      componentToRegister: SurveyViewComponent,
      pageName: 'Survey: ' + this._utils.displayString(survey?.name ?? null, 15),
      pageHeader: this.offering!.label,
      data: {
        id: survey?.uuid,
        survey: survey
      },
      id: this._utils.generateUuidWithPrefix('survey-view'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'survey-view',
      offering: this.offering
    }
    console.log('payload', payload);
    this._tabService.sendPageAddEvent(payload);
  }


}
